import {AbsoluteFill, useCurrentFrame} from 'remotion';

/**
 * SepiaTealGrade — global LUT overlay
 *
 * Applies a sepia-teal documentary color grade + vignette + grain
 * across the entire composition. Place at the TOP of the layer stack
 * so it sits above all media.
 *
 * @example
 * <AbsoluteFill>
 *   <KenBurns3D ... />
 *   <SepiaTealGrade intensity={0.35} vignette={0.4} grain={0.05} />
 * </AbsoluteFill>
 */

type Props = {
  intensity?: number;        // 0-1, default 0.35
  vignette?: number;         // 0-1 darken at edges, default 0.4
  grain?: number;            // 0-1 grain opacity, default 0.05
};

export const SepiaTealGrade: React.FC<Props> = ({
  intensity = 0.35,
  vignette = 0.4,
  grain = 0.05,
}) => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={{pointerEvents: 'none'}}>
      {/* Warm sepia in shadows */}
      <AbsoluteFill style={{
        background: 'radial-gradient(ellipse at center, rgba(107, 66, 38, 0) 0%, rgba(45, 55, 72, ' + intensity + ') 100%)',
        mixBlendMode: 'overlay',
      }} />
      {/* Cold teal in highlights */}
      <AbsoluteFill style={{
        background: 'linear-gradient(180deg, rgba(45, 55, 72, ' + (intensity * 0.5) + ') 0%, transparent 50%, rgba(107, 66, 38, ' + (intensity * 0.3) + ') 100%)',
        mixBlendMode: 'soft-light',
      }} />
      {/* Vignette */}
      <AbsoluteFill style={{
        background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0, 0, 0, ' + vignette + ') 100%)',
      }} />
      {/* Film grain (subtle animated noise) */}
      <AbsoluteFill style={{
        opacity: grain,
        backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'200\' height=\'200\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'' + (0.8 + (frame % 3) * 0.05) + '\' stitchTiles=\'stitch\'/></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\' opacity=\'0.8\'/></svg>")',
        backgroundSize: '200px 200px',
        mixBlendMode: 'overlay',
      }} />
    </AbsoluteFill>
  );
};
